import { useEffect, useRef, useState } from "react";
import { serviceCategories } from "../../../mocks/services";

export default function ServicesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="bg-charcoal-50 py-10 md:py-24 lg:py-28 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-8 md:mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-forest-600 text-xs font-semibold tracking-[0.25em] uppercase">
            What We Offer
          </span>
          <h2 className="font-cormorant text-2xl md:text-4xl lg:text-5xl font-semibold text-charcoal-900 mt-2 md:mt-3 mb-3 md:mb-4">
            Services Built Around You
          </h2>
          <p className="text-charcoal-500 text-xs md:text-base max-w-xl mx-auto leading-relaxed">
            Whether you&apos;re a growing business or managing personal finances, we have
            the right solution for your needs.
          </p>
        </div>

        {/* Category grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
          {serviceCategories.map((cat, i) => (
            <div
              key={cat.id}
              className={`group rounded-lg border bg-white transition-all duration-500 overflow-hidden ${
                cat.highlight
                  ? "border-forest-300 ring-1 ring-forest-200"
                  : "border-charcoal-100 hover:border-forest-300 hover:ring-1 hover:ring-forest-100"
              } ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 100 + 150}ms` }}
            >
              {cat.highlight && (
                <div className="bg-forest-600 px-4 md:px-8 py-1.5 md:py-2 flex items-center gap-2">
                  <i className="ri-star-line text-white text-xs" />
                  <span className="text-white text-xs font-semibold tracking-widest uppercase">
                    Sales Tax Specialist — incl. Florida
                  </span>
                </div>
              )}
              <div className="p-4 md:p-8">
                {/* Header row */}
                <div className="flex items-start gap-3 mb-4 md:mb-6">
                  <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-md bg-forest-50 border border-forest-100 flex-shrink-0">
                    <i className={`${cat.icon} text-forest-600 text-base md:text-xl`} />
                  </div>
                  <div>
                    <h3 className="font-cormorant text-base md:text-xl font-semibold text-charcoal-900">
                      {cat.title}
                    </h3>
                    <p className="text-charcoal-500 text-xs mt-0.5">{cat.description}</p>
                  </div>
                </div>

                {/* Service list */}
                <ul className="space-y-1.5 md:space-y-2.5 border-t border-charcoal-100 pt-3 md:pt-5">
                  {cat.items.map((item) => (
                    <li key={item} className="flex items-center gap-2.5 text-xs md:text-sm text-charcoal-600">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-forest-500 font-bold text-sm md:text-base" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-8 md:mt-14 rounded-lg bg-forest-600 px-5 md:px-10 py-6 md:py-10 flex flex-col items-center md:flex-row md:justify-between gap-4 md:gap-6 text-center md:text-left transition-all duration-700 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div>
            <p className="font-cormorant text-lg md:text-2xl font-semibold text-white mb-1">
              Not sure where to start?
            </p>
            <p className="text-forest-100 text-xs md:text-sm">
              Book a free 30-minute consultation — no commitment, just clarity.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 flex-shrink-0 w-full sm:w-auto">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-6 py-2.5 md:py-3 bg-white hover:bg-charcoal-50 text-forest-700 font-semibold rounded-md text-xs md:text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
            >
              Book Free Consultation
            </a>
            <a
              href="#services"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-6 py-2.5 md:py-3 bg-forest-700 hover:bg-forest-800 text-white rounded-md text-xs md:text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap border border-forest-500 text-center"
            >
              View All Services
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
